import React, { useCallback, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Flickity from "react-flickity-component";
import { ApolloError } from "apollo-boost";
import { Container } from "../Container/Container";
import { Product } from "../ListOfProducts/Product";
import { ProductProps } from "../../types/productTypes";
import SlideShowLoader from "./SlideShowLoader";
import { useWindowWidth } from "../../lib/hooks/useWindowWidth";

import "flickity/dist/flickity.css";
import "./SlideShow.scss";

interface SlideShowProps {
  items: any;
  title: string;
  loading: boolean;
  error: ApolloError | undefined;
  paddingLeft?: number;
  redirectLink?: string;
}

type Props = SlideShowProps;

export const SlideShow = (props: Props) => {
  const flickity = useRef<Flickity | null>(null);
  const [titleRef, getTitleRef] = useState<HTMLHeadingElement | null>(null);
  const history = useHistory();
  const windowWidth = useWindowWidth();
  const isTablet = windowWidth < 1024;
  const slideTitle = String(props.title).charAt(0).toUpperCase() + String(props.title).slice(1);

  const getTitleRefCallback = useCallback((c: HTMLHeadingElement | null) => {
    if (c !== null) {
      getTitleRef(c);
    }
  }, []);

  // Filter out out-of-stock products
  const inStockItems = props.items?.filter((product: any) => {
    if (!product || product.stockStatus === 'OUT_OF_STOCK' || product.stockQuantity === 0) {
      return false;
    }
    if (product.type === 'VARIABLE' && (!product.variations?.nodes || product.variations.nodes.length === 0)) {
      return false;
    }
    return true;
  }) || [];

  const mobileItems = inStockItems
    ?.map((item: any, i: number) => {
      return i % 2 === 0 && inStockItems?.slice(i, i + 2);
    })
    ?.filter((e: any) => e);

  if (props.loading) {
    return (
      <div className="SlideShow">
        <div className="SlideShow__wrapper">
          <SlideShowLoader />
        </div>
      </div>
    );
  }

  if (props.error) {
    return null;
  }

  return (
    <div className="SlideShow">
      <Container className="p-0-mobile d-flex justify-content-between">
        <h2 ref={getTitleRefCallback} className="font-weight-bold">
          {slideTitle}
        </h2>
        <div className="SlideShow__title d-flex">
          <span onClick={() => history.push(`/shop?${props.redirectLink}`)}>
            Shiko të gjitha
          </span>
        </div>
      </Container>
      <div className="SlideShow__wrapper">
        {!isTablet ? (
          <Flickity
            flickityRef={(c) => (flickity.current = c)}
            className={"carousel"}
            elementType={"div"}
            options={{
              wrapAround: false,
              draggable: true,
              cellAlign: inStockItems?.length <= 4 ? "center" : "left",
              freeScroll: true,
              resize: true,
              pageDots: !isTablet,
              prevNextButtons: false,
              contain: true,
              bgLazyLoad: true,
              lazyLoad: 1,
            }}
            disableImagesLoaded={false}
          >
            {!isTablet && (
              <div style={{ width: (titleRef?.offsetLeft || 0) - 13 }} />
            )}
            {inStockItems?.map((product: ProductProps, index: number) => {
              return (
                <div
                  key={`${product.id}-${index}`}
                  className="SlideShow-box"
                  id={`SlideShow-${product.slug}`}
                >
                  <Product product={product} key={`${product.id}-${index}`} />
                </div>
              );
            })}
            {!isTablet && (
              <div style={{ width: (titleRef?.offsetLeft || 0) - 13 }} />
            )}
          </Flickity>
        ) : (
          <div className="HomePageSlideShow--mobile">
            {mobileItems?.map((mobileItem: any, index: number) => {
              return (
                <div
                  key={index}
                  className="HomeSlider__slide d-flex flex-column"
                >
                  {mobileItem.map((item: any) => {
                    return (
                      <div
                        key={`${item.id}-${index}`}
                        className="HomeSlider__item"
                        id={`SlideShow-${item.slug}`}
                      >
                        <Product product={item} key={`${item.id}-${index}`} />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
